import {Injectable} from '@angular/core';
import {from, Observable, of, tap, throwError} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {doc, docSnapshots, Firestore} from '@angular/fire/firestore';
import {environment} from '../../environments/environment';
import {catchError, map, shareReplay, switchMap, take} from 'rxjs/operators';
import {Auth, authState, getIdTokenResult, signInWithEmailAndPassword, signOut} from '@angular/fire/auth';

import {OrganizationInterface, OrganizationInterfaceWithId} from '../_interfaces/organization.interface';
import {EhrClaims} from '../_interfaces/ehr-claims.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private organizationId?: string;
  private organization?: OrganizationInterfaceWithId;


  constructor(private firestore: Firestore,
              private auth: Auth,
              private httpClient: HttpClient) {
  }


  forgotPassword(email: string) {
    const url = environment.apiConfig.requestResetPasswordEmail;
    const body = {
      email,
      language: environment.language,
      isLocation: true,
    };
    return this.httpClient.post(url, body);
  }


  private getUserClaims(): Observable<EhrClaims> {
    return authState(this.auth).pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          return throwError(() => new Error($localize`:User not authenticated|error message:User not authenticated`));
        }
        return from(getIdTokenResult(user, true));
      }),
      map(result => result.claims as EhrClaims)
    );
  }

  getOrganizationId(): Observable<string> {
    if (this.organizationId) return of(this.organizationId);

    return this.getUserClaims().pipe(
      map(claims => {
        if (!claims.orgId) {
          throw new Error($localize`:Organization ID not found in claims|error message:Organization ID not found in claims`);
        }
        this.organizationId = claims.orgId;
        return claims.orgId;
      }),
      shareReplay(1)
    );
  }

  getOrganization(): Observable<OrganizationInterfaceWithId> {
    if (this.organization) return of(this.organization);

    return this.getUserClaims().pipe(
      switchMap(claims => {
        if (!claims.orgId) {
          return throwError(() => new Error($localize`:Organization ID not found in claims|error message:Organization ID not found in claims`));
        }
        const orgRef = doc(this.firestore, 'organizations', claims.orgId);
        return docSnapshots(orgRef);
      }),
      map(docSnap => ({
        ...(docSnap.data() as OrganizationInterface),
        id: docSnap.id,
        exists: docSnap.exists(),
      })),
      tap(org => (this.organization = org)),
      shareReplay(1)
    );
  }

  getOrganizationName$(): Observable<string> {
    return this.getOrganization().pipe(
      map(org => org.businessName),
      shareReplay(1)
    );
  }


  /**
   * Sends a verification code request to the backend.
   * @param token The user's ID token used for authorization.
   */
  requestVerificationCode(token: string): Observable<string> {
    const url = environment.apiConfig.requestVerificationCode;
    const body = {
      language: environment.language,
      isLocation: true
    };
    return this.httpClient.post<string>(url, body, {
      headers: {
        Accept: `application/json`,
        'Content-Type': `application/json`,
        Authorization: 'Bearer ' + token
      }
    });
  }


  /**
   * Authenticates the user without starting the verification-code request.
   * Keeping this step separate lets the UI distinguish credential failures from
   * failures while bootstrapping the second-factor session.
   * @param password The user's password.
   */
  signInWithEmailAndPassword(password: string): Observable<void> {
    const email = localStorage.getItem('authEmail');
    if (!email) {
      return throwError(() => ({code: 'auth/missing-email'}));
    }

    return from(signInWithEmailAndPassword(this.auth, email, password)).pipe(
      map(() => void 0),
      take(1)
    );
  }


  /**
   * Requests the Location verification code for the authenticated Firebase user.
   * This is intentionally a separate operation from password sign-in so an API,
   * token-refresh or network failure is never reported as a password failure.
   */
  requestVerificationCodeForCurrentUser(): Observable<string> {
    return authState(this.auth).pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          return throwError(() => new Error($localize`:User not authenticated|error message:User not authenticated`));
        }
        return from(getIdTokenResult(user, true));
      }),
      switchMap(tokenResult => this.requestVerificationCode(tokenResult.token)),
      take(1)
    );
  }


  /**
   * Backwards-compatible combined login flow for callers that still use it.
   * New UI flows should use the two explicit steps above.
   * @param password The user's password.
   */
  loginWithEmailAndPassword(password: string): Observable<string> {
    return this.signInWithEmailAndPassword(password).pipe(
      switchMap(() => this.requestVerificationCodeForCurrentUser()),
      take(1)
    );
  }


  /**
   * Verifies the provided code for office login, using a backend endpoint.
   * @param code The verification code.
   */
  verifyCode(code: string): Observable<void> {
    return authState(this.auth).pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          throw new Error($localize`:User not authenticated|error:User not authenticated`);
        }

        return from(getIdTokenResult(user, true)).pipe(
          switchMap(tokenResult => {
            const url = environment.apiConfig.verifyCodeForLocation;
            const body = {
              code,
              language: environment.language
            };

            return this.httpClient.post<{
              forcedUpdate?: boolean
            }>(
              url,
              body,
              {
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                  Authorization: 'Bearer ' + tokenResult.token
                }
              }
            );
          })
        );
      }),
      // 2) Force fresh claims after CFN has set them
      switchMap(() => from(this.auth.currentUser!.getIdToken(true))),
      map(() => void 0)
    );
  }


  /**
   * Clears cached data, resetting the session information and logs out the current user by signing them out of Firebase.
   */

  clearData(): Observable<boolean> {
    this.resetLocalState(); // always safe

    return authState(this.auth).pipe(
      take(1),
      switchMap((user) => {
        // Already signed out: nothing to validate, nothing to delete in Firestore.
        if (!user) {
          return of(true);
        }

        return from(getIdTokenResult(user, true)).pipe(
          map(r => (r?.claims ?? {}) as any),
          catchError(() => of({} as any)),
          switchMap((claims) =>
            from(signOut(this.auth)).pipe(
              catchError(() => of(null))
            )),
          map(() => true)
        );
      })
    );
  }

  private resetLocalState(): void {
    // --- reset in-memory state ---
    this.organizationId = undefined;
    this.organization = undefined;
  }


  checkInOut(employeeNumber: string) {
    const url = '';
    const body = {
      language: environment.language,
      employeeNumber,
    };
    return this.httpClient.post<string>(url, body);
  }


}
